<i18n>
{
  "ja": {
    "locationNotfound": "この住所からは有効な所在地が特定できませんでした。",
    "withinKmRadius": "{distance}km以内"
  }
}
</i18n>
<template>
  <div class="warehouse-search border-t border-gray-200">
    <div class="flex-grow md:flex w-full">
      <section class="search-pane absolute md:relative bg-white" :style="searchPaneStyle">
        <div ref="searchLocation" class="search-location">
          <header class="m-0 mb-1 md:mb-4">
            <h1 class="text-sm md:font-semibold md:text-2xl">
              {{ $t('warehouse.searchWarehouse') }}
            </h1>
          </header>
          <div class="relative">
            <input
              type="text"
              v-model="addressToSearch"
              :placeholder="$t('warehouse.letsEnterAddress')"
              size="medium"
              class="search-address input-text focus:border-secondary"
              clearable
              :disabled="!searchEnabled"
              @keypress.enter="onEnter"
            />
            <img class="icon_search clickable"
            src="@/assets/img/icon_search_green.svg"
            @click="onEnter" />
          </div>
        </div>
        <div>
          <div v-if="showList" ref="warehouseCount" class="warehouse-count px-4 py-3 text-header">
            <p v-html="$t('warehouse.warehouseCount', { count: warehouseCount })"></p>
          </div>
          <section
            v-if="showList"
            id="warehouse-list"
            ref="warehouseList"
            class="overflow-hidden"
            :style="listStyle"
            >
            <warehouse-list
              ref="warehouseListInner"
              :warehouses="warehouses"
              :selected-warehouse="selectedWarehouse"
              :is-mobile="isMobile"
              :list-height="listHeight"
              @select="onListSelected"
            />

          </section>
        </div>
      </section>
      <section class="map-pane flex-grow w-full h-full relative md:ml-1" :style="mapPaneStyle">
        <div v-if="searchEnabled" class="search-by-distance absolute z-10 w-60">
          <!-- <dropdown v-model="searchCriteria.distance" :options="searchDistanceOptions"  @change="onDistanceChange"
          class="distance-selector" /> -->
        </div>
        <div v-if="showDetail" class="warehouse-detail-popup absolute z-10">
          <div class="btn-close absolute rounded-full m-3 right-0 w-6 h-6 bg-gray-800 bg-opacity-75 text-center" @click="closePopup">
            <img class="w-full h-full" src="~/assets/img/cross_white.svg" />
          </div>
          <warehouse-item-detail v-if="detailedWarehouse.id" :warehouse="detailedWarehouse" :area="prefecture" />
        </div>
        <search-map
          ref="searchMap"
          :is-location-ready="locationReady"
          :is-loading="isLoading"
          :warehouses="warehouses"
          :map-center="center"
          :search-distance="searchDistance"
          :selected-warehouse="selectedWarehouse"
          @initialized="onMapInitialized"
          @location-found="onLocationFound"
          @select="onPinSelected"
          @unselect="unselectWarehouse"
          :search-location-height="searchLocationHeight"
        />
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '~assets/css/warehouse-search.scss';
</style>

<script>
import Jsonld from '@/components/mixins/jsonld';
import Dropdown from '@/components/dropdown';
import SearchMap from '@/components/warehouses/search/map/searchMap'
import WarehouseList from '@/components/warehouses/search/map/warehouseList';
import WarehouseFooter from '@/components/warehouses/footerMobile';
import WarehouseItemDetail from '@/components/warehouses/search/map/warehouseItemDetail';

import { mapGetters } from 'vuex';
import { isEmpty } from '@/components/utils/string';
import { debounce } from 'vue-debounce';

const DEFAULT_DISTANCE = 50;
const CENTER_OF_TOKYO = { lat: 35.6804, lng: 139.7690 };

// list offset = height of search-location div + warehouse-count div (desktop)
const LIST_OFFSET = 145 + 60;


// Reference: https://cloud.google.com/blog/products/maps-platform/how-calculate-distances-map-maps-javascript-api
const getStraightLineDistanceFromCenter = (cLat, cLng, mLat, mLng) => {
  if (cLat === mLat && cLng === mLng) return 0;
  const R = 6371.0710; // Radius of the Earth in kilometers
  const rlat1 = cLat * (Math.PI / 180); // Convert degrees to radians
  const rlat2 = mLat * (Math.PI / 180); // Convert degrees to radians
  const difflat = rlat2 - rlat1; // Radian difference (latitudes)
  const difflon = (mLng - cLng) * (Math.PI / 180); // Radian difference (longitudes)

  const d = 2 * R * Math.asin(Math.sqrt(Math.sin(difflat / 2) * Math.sin(difflat / 2) + Math.cos(rlat1) * Math.cos(rlat2) * Math.sin(difflon / 2) * Math.sin(difflon / 2)));
  return d;
}

const getMaxDistance = (warehouses, center) => {
  const maxDist = warehouses.reduce((max, wh) => {
    const dist = getStraightLineDistanceFromCenter(center.lat, center.lng, wh.lat, wh.lng);
    return Math.max(max, dist);
  }, 0);
  return maxDist;
}

const calculateCenterLocation = (warehouses) => {
  const centerLocation = warehouses.reduce(
    (accumulator, warehouse) => {
      return {
        latitude: accumulator.latitude + warehouse.lat || 0,
        longitude: accumulator.longitude + warehouse.lng || 0
      };
    },
    { latitude: 0, longitude: 0 }
  );

  const count = warehouses.length;
  return {
    lat: centerLocation.latitude / count,
    lng: centerLocation.longitude / count
  };
}

const isGeocodeValue = (value) => {
  if (isEmpty(value)) return false;
  const number = parseFloat(value);
  if (typeof number !== 'number') return false;

  return number >= -90 && number <= 90 || number >= -180 && number <= 180;
}

const getQueryParams = (query) => {
  const { lat, lng, address, distance } = query
  const dist = parseInt(distance);
  return {
    lat: isGeocodeValue(lat) ? parseFloat(lat) : null,
    lng: isGeocodeValue(lng) ? parseFloat(lng) : null,
    address: address || '',
    distance: isNaN(dist) ? DEFAULT_DISTANCE : dist
  }
}

const sortWarehouses = (warehouses) => {
  return warehouses.sort((a, b) => a.distance - b.distance);
}

export default {
  mixins: [Jsonld],
  components: { Dropdown, SearchMap, WarehouseList, WarehouseFooter, WarehouseItemDetail },
  meta: {
    feature: 'search'
  },
  async asyncData({ params, query, $warex, app, route }) {
    const { prefecture } = params;
    const hasPrefecture = Boolean(prefecture);
    let lat = null, lng = null;
    let center = { lat, lng }
    let sortedWarehouses = [];
    let mapRadius = DEFAULT_DISTANCE;
    let selectedWarehouse = { id: null, name: null };

    if (hasPrefecture) {
      // get center location by prefecture name

      const warehouses = await $warex.search({prefecture});
      const pinId = query.pin_id;
      let pinned;
      if (pinId) {
        pinned = warehouses.find(wh => wh.display_number === pinId);
        selectedWarehouse = { ...pinned, index: 0 };
        center = pinned ? { lat: pinned.lat, lng: pinned.lng } : null;
      } else {
        center = calculateCenterLocation(warehouses);
      }
      sortedWarehouses = sortWarehouses(warehouses);

      mapRadius = getMaxDistance(warehouses, center);
    }
    else {
      const { lat, lng } = getQueryParams(query);
      if (lat && lng ) center = { lat, lng };
    }
    return {
      warehouses: sortedWarehouses,
      selectedWarehouse,
      locationReady: hasPrefecture,
      isLoading: false,
      center,
      addressToSearch: hasPrefecture ? prefecture : '',
      breadcrumbs: [
        { url: '/warehouses/search/map', name: app.i18n.t('metaMapSearch.titleIndex') },
        { name: hasPrefecture ? prefecture : app.i18n.t('metaMapSearch.titleIndex') },
      ],
      searchCriteria: {
        lat,
        lng,
        address: '',
        distance:  Math.max(mapRadius, DEFAULT_DISTANCE)
      }
    };
  },
  data () {
    return {
      footerHeight: 0,
      onResize : function() {},
      defaultLocation: null,
      mapInitialized: false,
      currentLocation: null,
      searchLocationHeight: 0,
      popupClosed: false,
      detailedWarehouse: { id: null }
    };
  },

  computed: {
    global() {
      return this.$store.getters['cms/global']
    },
    homepage() {
      return this.$store.getters['cms/homepage']
    },
    ...mapGetters('nav', ['isMobile', 'clientHeight', 'clientWidth', 'headerHeight']),
    prefecture () {
      return this.$route.params.prefecture;
    },
    searchDistanceOptions () {
      return [10, 20, 30, 50, 100].map(dist => ({ label: this.$t('withinKmRadius', {distance: dist}), value: dist }));
    },
    isCenterLocationDetermined () {
      return this.center && this.center.lat && this.center.lng;
    },
    searchDistance () {
      if (isEmpty(this.searchCriteria.distance)) return 0;
      const distance = parseInt(this.searchCriteria.distance, 10);
      return distance;
    },
    searchEnabled () {
      return this.locationReady && !this.isLoading && this.mapInitialized;
    },
    showMap () {
      return this.isCenterLocationDetermined;
    },
    showDetail () {
      return this.selectedWarehouse.id && !this.popupClosed;
    },
    showList () {
      return this.warehouses.length > 0 && this.searchEnabled;
    },
    warehouseCount () {
      return this.warehouses.length;
    },
    searchPaneStyle () {
      const height = this.clientHeight - this.headerHeight;
      if (this.isMobile) {
        return {'top': '${this.headerHeight}px', 'height': `${height}px`, width: '100%' };
      } else {
        return { 'height': `${height}px`};
      }
    },
    mapPaneStyle () {
      if (this.isMobile) {
        return { 'padding-top': `${this.searchLocationHeight}px`};
      }
      return { 'padding-top': '0' };
    },
    listStyle () {
      if (this.isMobile) {
        return {'max-height': this.listMaxHeight, 'width': this.listWidth };
      } else {
        const height = this.listHeight - 1;
        return {'height': `${height}px`, 'width': this.listWidth };
      }
    },
    listMaxHeight () {
      return this.isMobile ? 'auto' : `${this.mapHeight - this.headerHeight}px`;
    },
    listHeight () {
      const height = this.clientHeight - this.headerHeight - LIST_OFFSET -1;
      return height;
    },
    listWidth () {
      // for the mobile screen, list width is the 100% of client window
      return this.isMobile ? `${this.clientWidth}px` : 'auto';
    }
  },

  async mounted () {
    await this.$nextTick();
    this.calculateElementHeight();
    this.onResize = debounce(this.calculateElementHeight, 500);
    window.addEventListener('resize', this.onResize);

    // if valid lat, lng values are in the query, use that value
    if (this.searchCriteria.lat && this.searchCriteria.lng) center = { lat: this.searchCriteria.lat, lng: this.searchCriteria.lng };

    // get geocode location from the browser
    try {
      this.currentLocation = await this.getCurrentLocation();
    } catch(e) {
      // silently fall back on the center of tokyo
      this.currentLocation = CENTER_OF_TOKYO;
    }

    if (this.prefecture) {
      // for prefecture search, the default distance is always the same
      this.$router.push({ path: this.$route.fullPath, query: {distance: DEFAULT_DISTANCE }});

      //keep area to later use it after registration
      localStorage.setItem('prefecture', this.prefecture);
    }
    else {
      this.setSearchFromQuery();
    }
  },

  beforeRouteLeave(to, from, next) {
    window.removeEventListener('resize', this.onResize);
    next();
  },

  methods: {
    calculateElementHeight () {
      const locStyle = getComputedStyle(this.$refs.searchLocation);
      this.searchLocationHeight = parseInt(locStyle.height);
    },
    async getCurrentLocation() {
      return new Promise((resolve, reject) => {
        if(!("geolocation" in navigator)) {
          reject(new Error('Geolocation is not available.'));
        }
        navigator.geolocation.getCurrentPosition(pos => {
          resolve(pos);
        }, err => {
          reject(err);
        });
      });
    },

    setSearchFromQuery () {
      const { lat, lng, address, distance } = getQueryParams(this.$route.query);

      if (!isEmpty(lat) && !isEmpty(lng)) {
        this.searchCriteria.lat = lat;
        this.searchCriteria.lng = lng;
      }
      this.searchCriteria.address = address;
      this.addressToSearch = address;
      this.searchCriteria.distance = distance;
    },

    updateSearchQuery ({ newCenter = null } = {}) {
      const origQuery = this.$route.query;
      let newQuery = { ...origQuery };

      if (!isEmpty(this.searchCriteria.address)) {
        newQuery = { ...origQuery, address: this.searchCriteria.address };
      }

      if (this.isCenterLocationDetermined) {
        newQuery = { ...newQuery, lat: this.center.lat.toString(), lng: this.center.lng.toString() };
        if (!this.prefecture) newQuery.address = this.addressToSearch;
      } else if (newCenter) {
        newQuery = { ...newQuery, lat: newCenter.toString(), lng: newCenter.lng.toString(), address: this.addressToSearch };
      }

      if (!isEmpty(this.searchCriteria.distance)) newQuery = { ...newQuery, distance: this.searchCriteria.distance.toString() };

      if (JSON.stringify(newQuery) != JSON.stringify(origQuery)) {
        let path = this.$route.fullPath;
        // if the address is changed on prefecture search, move the param prefecture to search address
        if (this.prefecture && origQuery.address !== newQuery.address) {
          path = '/warehouses/search/map/';
          newQuery.lat = this.center.lat;
          newQuery.lng = this.center.lng;
        }
        this.$router.push({ path: this.localePath(path), query: {...newQuery} });
      }
    },

    onMapInitialized () {
      this.mapInitialized = true;
      if (this.prefecture) return; // don't invoke another search for the prefecture page

      // We are invoking the search here for index page because geocode address can be called only after searchMap is initialized
      if (isEmpty(this.searchCriteria.address)) {
        this.search(this.center, this.searchCriteria.distance)
      }
      else {
        this.$refs.searchMap.invokeGeocodeAddress(this.searchCriteria.address);
      }
    },

    onDistanceChange (value) {
      this.searchCriteria.distance = value;
      this.updateSearchQuery();
      this.search(this.center, value);
    },

    async onEnter () {
      if (this.searchEnabled) {
        this.$refs.searchMap.invokeGeocodeAddress(this.addressToSearch);
      }
    },

    // this is only called when the address is geocoded
    onLocationFound (value) {
      if (value == null) {
        alert(this.$t('locationNotfound'));
      }
      // else the value is the { lat, lng } coordinate
      else {
        this.searchCriteria.address = this.addressToSearch;
        this.searchCriteria.distance = this.searchCriteria.distance || DEFAULT_DISTANCE;
        this.updateSearchQuery({ newCenter: value });
        this.search(value, this.searchCriteria.distance);
      }
    },

    closePopup () {
      this.popupClosed = true;
    },

    onListSelected (w, index) {
      this.selectWarehouse(w, index);
      this.popupClosed = false;
    },

    onPinSelected (w, index) {
      this.popupClosed = true; // don't open the detail popup when the pin is selected.
      this.selectWarehouse(w, index);
    },

    async selectWarehouse (w, index) {
      if (this.selectedWarehouse.id === w.id) {
        return this.unselectWarehouse();
      }
      // load warehouse detail from another API
      const detail =  await this.$warex.warehouse(w.display_number);
      this.detailedWarehouse = detail;
      this.selectedWarehouse = { ...detail, id: w.id, index};
    },
    unselectWarehouse () {
      this.selectedWarehouse = { id: null, name: null };
      this.popupClosed = true;
    },

    search (center, distance) {
      this.isLoading = true;
      this.warehouses = [];

      // DO NOT update the center location outside of this function
      if (center.lat && center.lng) {
        this.center = { lat: center.lat, lng: center.lng };
      }
      else {
        this.center = this.currentLocation;
      }
      this.$warex.search({ latitude: this.center.lat, longitude: this.center.lng, km: distance })
        .then((result) => {
          if (result.isError) throw new Error(result.message);
          this.warehouses = sortWarehouses(result);
        })
        .catch((e) => {
          console.error(`failed to get search results for '${query}'`, e);
          this.warehouses = [];
        })
        .finally(() => {
          this.locationReady = true; // center position is set
          this.isLoading = false;
        });
    }
  },

  head() {
    const { seo } = this.homepage;
    const { defaultSeo } = this.global;
    const metaTitle = this.prefecture ? this.$t('metaMapSearch.titleArea', { area: this.prefecture }) : this.$t('metaMapSearch.titleIndex')

    const fullSeo = {
      ...defaultSeo,
      ...seo,
      metaTitle,
      metaDescription: this.prefecture ? this.$t('metaMapSearch.descriptionArea', { area: this.prefecture }) : this.$t('metaMapSearch.description'),
      metaKeywords: `${this.$t('metaMapSearch.keywords')},${this.$t('keywords')}`
    };
    return {
      title: metaTitle,
      meta: [
        { hid: 'robots', name: 'robots', content: 'noindex, nofollow' },
        ...this.$options.filters.cmsMetaTags(fullSeo)
      ],
    }
  },
  jsonld() {
    const ret = []
    const breadcrumbs = this.getBreadcrumbsJsonld(this.breadcrumbs)
    breadcrumbs && ret.push(breadcrumbs)
    return ret
  }
}
</script>